import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import GalleryLightbox from '../components/GalleryLightbox';
import { imageUrl } from '../lib/images';

export default function OverdaekningPage() {
  const brochurePdf = 'https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/overdaekning.pdf';

  return (
    <>
      <SeoHead
        title="Overdækning - RB Glasfiber A/S"
        description="Download brochure. RB Glasfiber har udviklet et roterende overdæknings-koncept til minimering af lugtgener ved renseanlæg for industri og kommuner."
        canonical="https://www.rb-glasfiber.dk/rb-glasfiber/overdaekning"
        ogImage="https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/oversaekning-cropresize-1408x704.webp"
      />

      {/* Hero Header */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 bg-slate-950 text-white overflow-hidden border-b border-slate-800">
        <div className="absolute inset-0 z-0">
          <img
            src={imageUrl('https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/oversaekning-cropresize-1408x704.webp', 'width=1600,quality=80,format=auto')}
            alt="Overdækning til renseanlæg"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-950/60" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-[#1e9457]/20 border border-[#1e9457]/40 text-[#22c55e] text-xs font-bold uppercase tracking-widest mb-4">
              <i className="fa-solid fa-rotate"></i>
              <span>Miljøteknologi & Renseanlæg</span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-extrabold uppercase tracking-tight text-white mb-6">
              Overdækning
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
              RB Glasfiber har udviklet et roterende overdæknings-koncept til minimering af lugtgener ved renseanlæg.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href={brochurePdf}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded bg-[#1e9457] hover:bg-[#15703f] text-white font-bold uppercase tracking-wider text-sm shadow transition-all"
              >
                <i className="fa-solid fa-file-pdf"></i>
                <span>Download brochure (PDF)</span>
              </a>
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded bg-slate-800 hover:bg-slate-700 text-white font-semibold text-sm border border-slate-700 transition-all"
              >
                <span>Kontakt os om overdækning</span>
                <i className="fa-solid fa-arrow-right text-xs"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 text-xs font-bold text-[#1e9457] uppercase tracking-wider">
                <span className="w-2 h-2 rounded-full bg-[#1e9457]"></span>
                Effektiv sikring mod lugtgener og til-isning
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 uppercase">
                Roterende & stationære overdækninger
              </h2>
              <div className="prose text-slate-700 text-base leading-relaxed space-y-4">
                <p>
                  RB Glasfiber har udviklet et roterende overdæknings-koncept til minimering af lugtgener ved renseanlæg.
                </p>
                <p>
                  Den mest almindelige begrundelse for overdækning af renseanlæg er hensynet til lugtgener for det omgivende miljø. Hos Søholt Renseanlæg, Silkeborg sikres dette med bla. undertryk under overdækningen ved hjælp af en udsuger med tilhørende kulfilter.
                </p>
                <p>
                  Overdækningen opbygges af korrosionsbestandige profilerede plader i glasfiberarmeret polyester, der føres ud over bassinkanten og monteres med kraftig EPDM vulkaniseret gummiplade, og overdækkede 125 mm. specialhjul. En effektiv sikring mod til-isning.
                </p>
                <p>
                  Færdsel på- og ved overdækning under rotation, kan ske uden fare for fastklemning af personer.
                </p>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-xl overflow-hidden border border-slate-200 shadow-md aspect-[4/3] bg-slate-900">
                <img
                  src={imageUrl('https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/oversaekning-cropresize-1408x704.webp', 'width=700,quality=85,format=auto')}
                  alt="Overdækning til renseanlæg"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* Technical Data Specification Card */}
          <div className="p-8 sm:p-10 rounded-2xl bg-slate-900 text-white border border-slate-800 space-y-6">
            <h3 className="text-2xl font-bold uppercase tracking-tight text-white flex items-center gap-3">
              <i className="fa-solid fa-list-check text-[#1e9457]"></i>
              Teknisk data for overdækning
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              <div className="p-4 rounded bg-slate-800/80 border border-slate-700/80 flex items-start gap-3">
                <i className="fa-solid fa-check text-[#1e9457] mt-1"></i>
                <div>
                  <div className="font-semibold text-white">Fleksibel opbygning</div>
                  <div className="text-xs text-slate-300">Som roterende eller stationær</div>
                </div>
              </div>

              <div className="p-4 rounded bg-slate-800/80 border border-slate-700/80 flex items-start gap-3">
                <i className="fa-solid fa-check text-[#1e9457] mt-1"></i>
                <div>
                  <div className="font-semibold text-white">Materiale</div>
                  <div className="text-xs text-slate-300">Korrosionsbestandig glasfiber</div>
                </div>
              </div>

              <div className="p-4 rounded bg-slate-800/80 border border-slate-700/80 flex items-start gap-3">
                <i className="fa-solid fa-check text-[#1e9457] mt-1"></i>
                <div>
                  <div className="font-semibold text-white">Plader</div>
                  <div className="text-xs text-slate-300">Udskiftelige profilerede plader</div>
                </div>
              </div>

              <div className="p-4 rounded bg-slate-800/80 border border-slate-700/80 flex items-start gap-3">
                <i className="fa-solid fa-check text-[#1e9457] mt-1"></i>
                <div>
                  <div className="font-semibold text-white">Lugtudslip</div>
                  <div className="text-xs text-slate-300">Aqua vandlås mod lugtudslip</div>
                </div>
              </div>

              <div className="p-4 rounded bg-slate-800/80 border border-slate-700/80 flex items-start gap-3">
                <i className="fa-solid fa-check text-[#1e9457] mt-1"></i>
                <div>
                  <div className="font-semibold text-white">Sikkerhed</div>
                  <div className="text-xs text-slate-300">Gangareal på kørebro i skridsikker glasfiber</div>
                </div>
              </div>

              <div className="p-4 rounded bg-slate-800/80 border border-slate-700/80 flex items-start gap-3">
                <i className="fa-solid fa-check text-[#1e9457] mt-1"></i>
                <div>
                  <div className="font-semibold text-white">Servicevenlig</div>
                  <div className="text-xs text-slate-300">Optagelige gangbro for optimal servicering af skraber mv.</div>
                </div>
              </div>

              <div className="p-4 rounded bg-slate-800/80 border border-slate-700/80 flex items-start gap-3">
                <i className="fa-solid fa-check text-[#1e9457] mt-1"></i>
                <div>
                  <div className="font-semibold text-white">Finish</div>
                  <div className="text-xs text-slate-300">Ingen synlige bolte og møtrikker</div>
                </div>
              </div>

              <div className="p-4 rounded bg-slate-800/80 border border-slate-700/80 flex items-start gap-3">
                <i className="fa-solid fa-check text-[#1e9457] mt-1"></i>
                <div>
                  <div className="font-semibold text-white">Filtrering</div>
                  <div className="text-xs text-slate-300">Kulfilter mod luftgener</div>
                </div>
              </div>

              <div className="p-4 rounded bg-slate-800/80 border border-slate-700/80 flex items-start gap-3">
                <i className="fa-solid fa-check text-[#1e9457] mt-1"></i>
                <div>
                  <div className="font-semibold text-white">Belastning</div>
                  <div className="text-xs text-slate-300">Last op til 100 kN pr m2</div>
                </div>
              </div>

              <div className="p-4 rounded bg-slate-800/80 border border-slate-700/80 flex items-start gap-3">
                <i className="fa-solid fa-check text-[#1e9457] mt-1"></i>
                <div>
                  <div className="font-semibold text-white">Vægt</div>
                  <div className="text-xs text-slate-300">Lav vægt, skånsom for konstruktionen</div>
                </div>
              </div>

              <div className="p-4 rounded bg-slate-800/80 border border-slate-700/80 flex items-start gap-3">
                <i className="fa-solid fa-check text-[#1e9457] mt-1"></i>
                <div>
                  <div className="font-semibold text-white">Farve</div>
                  <div className="text-xs text-slate-300">Farve efter ønske</div>
                </div>
              </div>

              <div className="p-4 rounded bg-slate-800/80 border border-slate-700/80 flex items-start gap-3">
                <i className="fa-solid fa-check text-[#1e9457] mt-1"></i>
                <div>
                  <div className="font-semibold text-white">Størrelse</div>
                  <div className="text-xs text-slate-300">Størrelse efter ønske</div>
                </div>
              </div>
            </div>
          </div>

          {/* Lightbox Gallery */}
          <div className="pt-8 border-t border-slate-200">
            <GalleryLightbox
              gallerySlug="overdaekning"
              title="Fotogalleri: Overdækning"
              description="Billeder af roterende overdæknings-koncept samt den store Ø27 meter overdækning til Søholt Renseanlæg."
              columns={2}
            />
          </div>
        </div>
      </section>
    </>
  );
}
